/**
 * Job-scoped session draft for Apply with Ari (TP-498).
 * Key: twine_apply_ari_draft_{briefId}
 * Answers live client-side until email → passwordless auth; then
 * `/projects/:id/apply-ari/complete` persists profile + sends pitch.
 */

import {
  buildSignupAriPersistPayload,
  buildSignupAriLocationLabel,
} from 'helpers/signupAriDraft';

export const APPLY_ARI_DRAFT_KEY_PREFIX = 'twine_apply_ari_draft_';
export const APPLY_ARI_PENDING_SEARCH_KEY = 'twine_apply_ari_pending_search';

/** Who started the application — guests are signed in by the complete page. */
export const APPLY_ARI_ORIGIN_GUEST = 'guest';
export const APPLY_ARI_ORIGIN_SIGNED_IN = 'signed-in';
const APPLY_ARI_ORIGINS = [APPLY_ARI_ORIGIN_GUEST, APPLY_ARI_ORIGIN_SIGNED_IN];

/** Guest APPLY_SEQ — profile onboarding + apply questions + email. */
export const APPLY_ARI_STEPS = [
  'name',
  'country',
  'city',
  'roles',
  'experience',
  'bio',
  'travel',
  'fee',
  'pitchAsk',
  'pitch',
  'email',
];

/**
 * Signed-in MEMBER_SEQ (TP-500). Profile questions (name → bio) are asked
 * only when the profile is missing them — callers filter with
 * getApplyAriStepsForMode({ missingProfileSteps }).
 */
export const APPLY_ARI_MEMBER_STEPS = [
  'name',
  'country',
  'city',
  'roles',
  'experience',
  'bio',
  'travel',
  'fee',
  'pitchAsk',
  'pitch',
];

/** Onboarding questions a signed-in profile may already have answered. */
export const APPLY_ARI_PROFILE_STEPS = [
  'name',
  'country',
  'city',
  'roles',
  'experience',
  'bio',
];

/** Signup experience labels ↔ preferences.email.job_alert_experience_level. */
const EXPERIENCE_LABEL_TO_PREF_KEY = {
  Amateur: 'amateur',
  Beginner: 'beginner',
  Junior: 'junior',
  'Mid-level': 'mid_level',
  Senior: 'senior',
  Expert: 'expert',
};

/**
 * Profile display name, or '' when it's missing or the API's default
 * `user<id>` placeholder (same rule as the Ari page greeting).
 * @param {Object|null|undefined} user
 * @returns {string}
 */
export function getApplyAriProfileName(user) {
  const name = String((user && user.displayname) || '').trim();
  return /^user\d+$/i.test(name) ? '' : name;
}

const isPrefOn = (value) =>
  value === true || value === 1 || value === '1' || value === 'true';

/**
 * Experience labels switched on in the user's job alert preferences.
 * @param {Object|null|undefined} user
 * @returns {string[]}
 */
export function getUserApplyAriExperience(user) {
  const levels =
    user &&
    user.preferences &&
    user.preferences.email &&
    user.preferences.email.job_alert_experience_level;
  if (!levels || typeof levels !== 'object') return [];
  return Object.keys(EXPERIENCE_LABEL_TO_PREF_KEY).filter((label) => {
    const key = EXPERIENCE_LABEL_TO_PREF_KEY[label];
    // Transformers have used both mid_level and mid-level.
    return isPrefOn(levels[key]) || isPrefOn(levels[key.replace('_', '-')]);
  });
}

/**
 * Onboarding questions the signed-in profile hasn't answered yet. City is
 * optional in onboarding, so it is asked only alongside a missing country.
 * @param {Object|null|undefined} user
 * @returns {string[]}
 */
export function getApplyAriMissingProfileSteps(user) {
  if (!user || typeof user !== 'object') return [...APPLY_ARI_PROFILE_STEPS];
  const location =
    user.location && typeof user.location === 'object' ? user.location : {};
  const hasCountry = Boolean(
    String(location.country || location.country_code || '').trim()
  );
  const hasRoles =
    Array.isArray(user.featured_roles) &&
    user.featured_roles.some((role) => String(role || '').trim());
  const missing = {
    name: !getApplyAriProfileName(user),
    country: !hasCountry,
    city: !hasCountry,
    roles: !hasRoles,
    experience: getUserApplyAriExperience(user).length === 0,
    bio: !String(user.bio || '').trim(),
  };
  return APPLY_ARI_PROFILE_STEPS.filter((step) => missing[step]);
}

/**
 * Step list for guest vs signed-in Apply with Ari. Signed-in experts only get
 * the profile questions listed in `missingProfileSteps`. Remote jobs skip
 * travel and unpaid jobs skip the fee (matches CreativePitch, which hides
 * both; the API rejects a fee on unpaid jobs).
 * @param {{ signedIn?: boolean, missingProfileSteps?: string[], remote?: boolean, paid?: boolean }} [opts]
 * @returns {string[]}
 */
export function getApplyAriStepsForMode({
  signedIn = false,
  missingProfileSteps = [],
  remote = false,
  paid = true,
} = {}) {
  const base = signedIn ? APPLY_ARI_MEMBER_STEPS : APPLY_ARI_STEPS;
  return base.filter(
    (step) =>
      !(
        signedIn &&
        APPLY_ARI_PROFILE_STEPS.includes(step) &&
        !missingProfileSteps.includes(step)
      ) &&
      !(remote && step === 'travel') &&
      !(paid === false && step === 'fee')
  );
}

const EMPTY_ANSWERS = {
  name: '',
  country: '',
  countryCode: '',
  city: '',
  roles: [],
  experience: [],
  bio: '',
  travel: null,
  fee: null,
  feeAmount: 0,
  pitchAsk: null,
  pitch: '',
  // Signed-in only: share the CV on file with this application (TP-500).
  includeCv: null,
  email: '',
};

function canUseSessionStorage() {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(window.sessionStorage);
  } catch (e) {
    return false;
  }
}

/**
 * @param {string|number} briefId
 * @returns {string}
 */
export function getApplyAriDraftKey(briefId) {
  return `${APPLY_ARI_DRAFT_KEY_PREFIX}${String(briefId || '').trim()}`;
}

/**
 * Apply with Ari chat path for a job.
 * @param {string} routeId - URL id (encoded slug or numeric)
 * @param {string} [search] - query string to keep, e.g. the variant `?apply=ari`
 */
export function getApplyAriPath(routeId, search = '') {
  const query = String(search || '').replace(/^\?/, '');
  return `/projects/${routeId}/apply-ari${query ? `?${query}` : ''}`;
}

/**
 * Auth-only complete path after passwordless verify.
 * @param {string} routeId
 */
export function getApplyAriCompletePath(routeId) {
  return `/projects/${routeId}/apply-ari/complete`;
}

export function getEmptyApplyAriAnswers() {
  return {
    ...EMPTY_ANSWERS,
    roles: [],
    experience: [],
    travel: null,
    fee: null,
    feeAmount: 0,
    pitchAsk: null,
  };
}

/**
 * @param {string|number} briefId
 * @param {{ allowedSteps?: string[] }} [opts] - when set, activeStep must be in this list
 * @returns {{ answers: Object, activeStep: string, completedSteps: string[], origin: string|null } | null}
 */
export function loadApplyAriDraft(briefId, { allowedSteps } = {}) {
  if (!canUseSessionStorage() || !briefId) return null;
  try {
    const raw = window.sessionStorage.getItem(getApplyAriDraftKey(briefId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const steps =
      Array.isArray(allowedSteps) && allowedSteps.length
        ? allowedSteps
        : APPLY_ARI_STEPS;
    const fallbackStep = steps[0] || 'name';
    return {
      answers: {
        ...getEmptyApplyAriAnswers(),
        ...(parsed.answers && typeof parsed.answers === 'object'
          ? parsed.answers
          : {}),
      },
      activeStep:
        typeof parsed.activeStep === 'string' &&
        steps.includes(parsed.activeStep)
          ? parsed.activeStep
          : fallbackStep,
      completedSteps: Array.isArray(parsed.completedSteps)
        ? parsed.completedSteps.filter((id) => steps.includes(id))
        : [],
      origin: APPLY_ARI_ORIGINS.includes(parsed.origin) ? parsed.origin : null,
    };
  } catch (e) {
    return null;
  }
}

/**
 * Seed apply answers from an authenticated Twine profile (TP-500).
 * @param {Object|null|undefined} user
 * @returns {Object}
 */
export function seedApplyAriAnswersFromUser(user) {
  const empty = getEmptyApplyAriAnswers();
  if (!user || typeof user !== 'object') return empty;

  const name = getApplyAriProfileName(user);
  const location =
    user.location && typeof user.location === 'object' ? user.location : {};
  const country = String(location.country || '').trim();
  const countryCode = String(location.country_code || '').trim();
  const city = String(location.city || '').trim();
  const roles = Array.isArray(user.featured_roles)
    ? user.featured_roles.map((r) => String(r).trim()).filter(Boolean)
    : [];
  const bio = String(user.bio || '').trim();
  const email = String(user.email || '').trim();

  return {
    ...empty,
    name,
    country,
    countryCode,
    city,
    roles,
    experience: getUserApplyAriExperience(user),
    bio,
    email,
  };
}

/**
 * @param {string|number} briefId
 * @param {{ answers: Object, activeStep: string, completedSteps: string[], origin?: string }} draft
 * @returns {boolean} whether the draft was stored (the complete page needs it)
 */
export function saveApplyAriDraft(briefId, draft) {
  if (!canUseSessionStorage() || !briefId || !draft) return false;
  try {
    window.sessionStorage.setItem(
      getApplyAriDraftKey(briefId),
      JSON.stringify({
        answers: { ...getEmptyApplyAriAnswers(), ...(draft.answers || {}) },
        activeStep: draft.activeStep || 'name',
        completedSteps: Array.isArray(draft.completedSteps)
          ? draft.completedSteps
          : [],
        origin: APPLY_ARI_ORIGINS.includes(draft.origin) ? draft.origin : null,
        savedAt: Date.now(),
      })
    );
    return true;
  } catch (e) {
    // Quota / private mode — the chat still works in memory.
    return false;
  }
}

/**
 * @param {string|number} briefId
 */
export function clearApplyAriDraft(briefId) {
  if (!canUseSessionStorage() || !briefId) return;
  try {
    window.sessionStorage.removeItem(getApplyAriDraftKey(briefId));
  } catch (e) {
    // ignore
  }
}

/** Mark that `/ari` should auto-run a search once after Launch Ari from summary. */
export function markApplyAriPendingSearch() {
  if (!canUseSessionStorage()) return;
  try {
    window.sessionStorage.setItem(APPLY_ARI_PENDING_SEARCH_KEY, '1');
  } catch (e) {
    // ignore
  }
}

export function hasApplyAriPendingSearch() {
  if (!canUseSessionStorage()) return false;
  try {
    return window.sessionStorage.getItem(APPLY_ARI_PENDING_SEARCH_KEY) === '1';
  } catch (e) {
    return false;
  }
}

/**
 * Read and clear the pending auto-search flag (one-shot).
 * @returns {boolean}
 */
export function consumeApplyAriPendingSearch() {
  if (!canUseSessionStorage()) return false;
  try {
    const pending =
      window.sessionStorage.getItem(APPLY_ARI_PENDING_SEARCH_KEY) === '1';
    if (pending) {
      window.sessionStorage.removeItem(APPLY_ARI_PENDING_SEARCH_KEY);
    }
    return pending;
  } catch (e) {
    return false;
  }
}

/** Profile fields each onboarding question writes. */
const PROFILE_FIELDS_BY_STEP = {
  name: ['displayname'],
  country: ['location'],
  city: ['location'],
  roles: ['featured_roles'],
  experience: ['preferences'],
  bio: ['bio'],
};

/**
 * Profile + Ari criteria from apply answers (reuses signup payload builder).
 * Bio is added to profile when present. Pass `answeredSteps` to only write
 * the profile fields those questions cover, so a signed-in apply never
 * rewrites profile data it didn't ask about (criteria still use everything).
 * @param {Object} answers
 * @param {{ answeredSteps?: string[] }} [opts]
 */
export function buildApplyAriPersistPayload(
  answers = {},
  { answeredSteps } = {}
) {
  const base = buildSignupAriPersistPayload({
    ...answers,
    // Apply flow skips newsletter — leave unset so prefs are not forced.
    newsletter: null,
  });
  const bio = String(answers.bio || '').trim();
  if (bio) {
    base.profile.bio = bio;
  }
  if (Array.isArray(answeredSteps)) {
    const allowed = new Set(
      answeredSteps.reduce(
        (fields, step) => fields.concat(PROFILE_FIELDS_BY_STEP[step] || []),
        []
      )
    );
    Object.keys(base.profile).forEach((field) => {
      if (!allowed.has(field)) delete base.profile[field];
    });
  }
  return base;
}

export { buildSignupAriLocationLabel };
